import React, { useState, useEffect } from 'react';
import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography, tableCellClasses, Chip } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchJournalEntriesPartyReport,
  downloadJournalEntriesPartyReportPdf,
  downloadJournalEntriesPartyReportExcel
} from '../../store/reportStore/reportThunk';
import { clearReportsData } from '../../store/reportStore/reportslice';
import ReusableReportHeader from '../../components/Reports/ReusableReportHeader';
import Filters from '../../components/Filters/Filters';
import { ApiStatus } from '../../utils/api.types';
import { VocuherCategory, Utils } from '../../utils/common-utils';
import { toast } from 'react-toastify';
import styled_ from "@emotion/styled";

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({}));

const ExpensesJournalEntriesReportPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { reportsData, status, errorMessage } = useAppSelector((state) => state.reports);
  const { selectedAccount } = useAppSelector((state) => state.auth);
  const accountId = selectedAccount?._id;

  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);

  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  // Fetch report when filters change
  useEffect(() => {
    if (accountId && filters) {
      setIsLoading(true);
      dispatch(fetchJournalEntriesPartyReport({
        accountId,
        params: filters
      })).finally(() => {
        setIsLoading(false);
      });
    }
  }, [accountId, filters, dispatch]);

  // Clear data when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearReportsData());
    };
  }, [dispatch]);

  const handleDownloadPdf = () => {
    if (!accountId || !filters) {
      toast.error("Please select date range to download PDF");
      return false;
    }

    return dispatch(downloadJournalEntriesPartyReportPdf({
      accountId,
      params: `${filters}&isDownloadPdf=true`
    })).unwrap().then((data: any) => {
      if (data) {
        const blob = new Blob([data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        a.download = `Expense_Journal_Entries_Report_${timestamp}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  };

  const handleDownloadExcel = () => {
    if (!accountId || !filters) {
      toast.error("Please select date range to download Excel");
      return false;
    }

    return dispatch(downloadJournalEntriesPartyReportExcel({
      accountId,
      params: `${filters}&isDownloadExcel=true`
    })).unwrap().then((data: any) => {
      if (data) {
        const blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        a.download = `Expense_Journal_Entries_Report_${timestamp}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  };

  const handleShare = () => {
    toast.info("Share functionality will be implemented soon");
  };

  // Get status chip color
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Booked':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ width: 'calc(100% - 7px)', height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
      {/* Scrollable content area */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        minHeight: 0,
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        {/* Report Header */}
        <ReusableReportHeader
          reportType="EXPENSE_JOURNAL_ENTRIES"
          title="Expense Journal Entries Report"
          subtitle={filterData?.periodString || ''}
          recordCount={reportsData ? reportsData.length : 0}
          isLoading={isLoading || status === ApiStatus.LOADING}
          onDownloadPDF={handleDownloadPdf}
          onDownloadExcel={handleDownloadExcel}
          onShare={handleShare}
          showPrint={false}
        />

        {/* Filters */}
        <Box px={2} py={1.5}>
          <Filters
            type={VocuherCategory.PURCHASE}
            filterHandler={filterOnChangeHandler}
            selectedFilters={filterdOnChangeHandler}
            hideFilter={true}
            hidePartySearch={false}
          />
        </Box>

        {/* Report Table */}
        <Box mt={2}>
          {status === ApiStatus.LOADING || isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                Loading expense journal entries...
              </Typography>
            </Box>
          ) : errorMessage ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="error">
                Error: {errorMessage}
              </Typography>
            </Box>
          ) : !reportsData || reportsData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No expense journal entries found. Please adjust your filters.
              </Typography>
            </Box>
          ) : (
            <Table stickyHeader sx={{ minWidth: 700, marginBottom: '20px' }} size='small' aria-label="expense journal entries table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Date</StyledTableCell>
                  <StyledTableCell>Vendor Name</StyledTableCell>
                  <StyledTableCell>Expense Category</StyledTableCell>
                  <StyledTableCell>Expense Ledger</StyledTableCell>
                  <StyledTableCell align="right">Gross Amount</StyledTableCell>
                  <StyledTableCell align="right">TDS Amount</StyledTableCell>
                  <StyledTableCell align="right">Net Payable</StyledTableCell>
                  <StyledTableCell align="center">Status</StyledTableCell>
                  <StyledTableCell>Payment Mode</StyledTableCell>
                  <StyledTableCell>Paid Date</StyledTableCell>
                  <StyledTableCell>Narration</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportsData.map((row: any, index: number) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>{row.date}</StyledTableCell>
                    <StyledTableCell>{row.vendorName}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {row.expenseCategoryColor && (
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: row.expenseCategoryColor,
                              flexShrink: 0
                            }}
                          />
                        )}
                        <Typography variant="body2" sx={{ fontSize: '9pt' }}>
                          {row.expenseCategory}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>{row.expenseLedger}</StyledTableCell>
                    <StyledTableCell align="right">
                      {Utils.formatNumber(row.grossAmount, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {row.tdsAmount && parseFloat(row.tdsAmount) > 0 ? (
                        <Box sx={{ color: '#dc2626' }}>
                          {Utils.formatNumber(row.tdsAmount, false, true)}
                          {row.tdsRate && (
                            <Typography component="span" sx={{ fontSize: '7.5pt', ml: 0.5, color: '#6b7280' }}>
                              ({row.tdsRate}%)
                            </Typography>
                          )}
                        </Box>
                      ) : '-'}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 600 }}>
                      {Utils.formatNumber(row.netPayableAmount, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Chip
                        label={row.status}
                        color={getStatusChipColor(row.status) as any}
                        size="small"
                        sx={{ fontSize: '8pt', height: '20px' }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>{row.paymentMode}</StyledTableCell>
                    <StyledTableCell>{row.paidDate}</StyledTableCell>
                    <StyledTableCell>{row.narration || '-'}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExpensesJournalEntriesReportPage;
